import type { QueryClient } from '@tanstack/react-query'
import type { CrateItem, MissingCrateItem, OrderDetails } from '@/entities/order/model/order.types'
import type { StateConfig } from '@/entities/state/model/state.types'
import { orderKeys } from '@/entities/order/model/order.queryKeys'
import { stateKeys } from '@/entities/state/model'
import { getExpectedCrateItemsForZebraOrder } from './order.crate'
import { isZebraScanProgressStatus, shouldDiscardZebraScanState } from './order.zebraScan'
import { removeZebraEventsForOrder } from './zebraEventQueue'
import { ZEBRA_ORDER_SCAN_DISCARDED_EVENT } from './zebraScan.constants'
import { readAllZebraOrderScanStates, writeAllZebraOrderScanStates } from './zebraScanStorage'

interface ZebraOrderScanDiscardedDetail {
  orderId: string
}

function dispatchDiscarded(orderId: string) {
  if (typeof window === 'undefined') {
    return
  }
  window.dispatchEvent(
    new CustomEvent<ZebraOrderScanDiscardedDetail>(ZEBRA_ORDER_SCAN_DISCARDED_EVENT, {
      detail: { orderId },
    }),
  )
}

/**
 * Merges server-side crateScan state with the device's local offline state.
 * Server wins on barcode/crateType collisions. A missing entry is only discarded
 * when ALL expected crates of that type are accounted for in the merged scan.
 */
function mergeScanState(
  serverCrateScan: OrderDetails['crateScan'],
  local: { scannedBarcodes: CrateItem[], missingCrates: MissingCrateItem[] },
  expectedItems: CrateItem[],
): { scannedBarcodes: CrateItem[], missingCrates: MissingCrateItem[] } {
  const serverScanned = serverCrateScan?.scannedBarcodes ?? []
  const serverMissing = serverCrateScan?.missingCrates ?? []

  // Union server + local scanned, deduplicated by barcode; server wins on collision
  const scannedByBarcode = new Map<string, CrateItem>()
  for (const item of [...local.scannedBarcodes, ...serverScanned]) {
    scannedByBarcode.set(item.barcode, item)
  }
  const mergedScanned = Array.from(scannedByBarcode.values())

  // Count expected and scanned per crateType so we only drop a missing entry when
  // every expected crate of that type has been scanned (partial scans stay missing).
  const expectedCountByType = new Map<string, number>()
  for (const item of expectedItems) {
    expectedCountByType.set(item.crateType, (expectedCountByType.get(item.crateType) ?? 0) + 1)
  }
  const scannedCountByType = new Map<string, number>()
  for (const item of mergedScanned) {
    scannedCountByType.set(item.crateType, (scannedCountByType.get(item.crateType) ?? 0) + 1)
  }

  // Union server + local missing, deduplicated by crateType; server wins on collision
  const missingByCrateType = new Map<string, MissingCrateItem>()
  for (const item of [...local.missingCrates, ...serverMissing]) {
    missingByCrateType.set(item.crateType, item)
  }
  const mergedMissing = Array.from(missingByCrateType.values())
    .filter((m) => {
      const scanned = scannedCountByType.get(m.crateType) ?? 0
      const expected = expectedCountByType.get(m.crateType) ?? 0
      return scanned < expected
    })

  return { scannedBarcodes: mergedScanned, missingCrates: mergedMissing }
}

/**
 * After fresh orders are in the TanStack cache, reconcile persisted Zebra scan data:
 * - If the order is still in the Zebra progress window, merge server + local scan state (server wins
 *   on conflicts, scan beats missing) and write both to cache and zebraScanStorage.
 * - Otherwise clear local persistence so stale offline work is not applied later.
 *
 * BD-8166: storage is read once and written once per pass, and every cache patch goes out in a
 * single setQueryData. This runs inside the orders cache subscriber, which TanStack notifies
 * synchronously - patching order by order re-entered this function once per patch, which cost
 * N(N+1)/2 passes (each parsing the whole blob once per order) and froze the board for minutes.
 */
export function reconcileZebraOfflineScanWithOrdersCache(queryClient: QueryClient): void {
  // Tutorial owns order 100001 scan phases in RQ only — do not re-merge device LS into the board.
  if (typeof globalThis.window !== 'undefined' && globalThis.window.__tutorialMode) {
    return
  }

  const orders = queryClient.getQueryData<OrderDetails[]>(orderKeys.orders())
  if (!orders) {
    return
  }
  const stateConfig = queryClient.getQueryData<StateConfig>(stateKeys.stateConfig())
  const scanStates = readAllZebraOrderScanStates()
  let scanStatesChanged = false

  // Keep expected crate-item cache warm for all active Zebra-progress orders.
  // This allows metadata-gap fallback even before any order details modal has been opened.
  for (const order of orders) {
    if (!isZebraScanProgressStatus(order.status, stateConfig?.states)) {
      continue
    }
    const expectedItems = getExpectedCrateItemsForZebraOrder(order)
    if (expectedItems.length === 0) {
      continue
    }
    const local = scanStates[order.orderId]
    if (
      JSON.stringify(local?.expectedCrateItems ?? []) === JSON.stringify(expectedItems)
    ) {
      continue
    }
    scanStates[order.orderId] = {
      scannedBarcodes: local?.scannedBarcodes ?? [],
      missingCrates: local?.missingCrates ?? [],
      expectedBarcodes: expectedItems.map(item => item.barcode),
      expectedCrateItems: expectedItems,
      updatedAt: new Date().toISOString(),
    }
    scanStatesChanged = true
  }

  const ordersById = new Map(orders.map(order => [order.orderId, order]))
  const crateScanPatches = new Map<string, OrderDetails['crateScan']>()
  const discardedOrderIds: string[] = []

  for (const [orderId, local] of Object.entries(scanStates)) {
    const order = ordersById.get(orderId)

    if (!order || shouldDiscardZebraScanState(order.status, stateConfig?.states)) {
      delete scanStates[orderId]
      scanStatesChanged = true
      discardedOrderIds.push(orderId)
      continue
    }

    const expectedItems = getExpectedCrateItemsForZebraOrder(order)
    const expectedBarcodes = expectedItems.length > 0
      ? expectedItems.map(item => item.barcode)
      : (order.crateScan?.expectedBarcodes ?? [])

    const device = order.crateScan?.device?.fingerprint
      ? { type: 'zebra' as const, fingerprint: order.crateScan.device.fingerprint }
      : { type: 'zebra' as const }

    const { scannedBarcodes, missingCrates } = mergeScanState(order.crateScan, local, expectedItems)

    // Write resolved state back so subsequent reconcile runs don't re-introduce stale data
    if (
      JSON.stringify(local.scannedBarcodes) !== JSON.stringify(scannedBarcodes)
      || JSON.stringify(local.missingCrates) !== JSON.stringify(missingCrates)
    ) {
      scanStates[orderId] = { ...local, scannedBarcodes, missingCrates, updatedAt: new Date().toISOString() }
      scanStatesChanged = true
    }

    const nextCrateScan = {
      expectedBarcodes,
      scannedBarcodes,
      missingCrates,
      device,
    }

    const current = order.crateScan
    if (
      current
      && JSON.stringify(current.scannedBarcodes) === JSON.stringify(nextCrateScan.scannedBarcodes)
      && JSON.stringify(current.missingCrates) === JSON.stringify(nextCrateScan.missingCrates)
      && JSON.stringify(current.expectedBarcodes) === JSON.stringify(nextCrateScan.expectedBarcodes)
      && current.device?.type === nextCrateScan.device.type
      && current.device?.fingerprint === nextCrateScan.device.fingerprint
    ) {
      continue
    }
    crateScanPatches.set(orderId, nextCrateScan)
  }

  // Storage first: the cache patch below re-enters this function, which must see the resolved state.
  if (scanStatesChanged) {
    writeAllZebraOrderScanStates(scanStates)
  }

  if (crateScanPatches.size > 0) {
    queryClient.setQueryData<OrderDetails[]>(orderKeys.orders(), old => old?.map(o => (
      crateScanPatches.has(o.orderId) ? { ...o, crateScan: crateScanPatches.get(o.orderId) } : o
    )))
  }

  for (const orderId of discardedOrderIds) {
    removeZebraEventsForOrder(orderId)
    dispatchDiscarded(orderId)
  }
}
